import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

import {
  useAuthentication,
  useApi,
  useUser,
  useNotification,
  useAgentExtension,
  useLocalStorage,
} from '../../hooks';
import { LoginData, ProfileItem } from '../../services/api/types';
import { CvLayout } from './CvLayout';
import { SettingsSidebar } from './SettingsSidebar';
import { CommonCard } from './CommonCard';
import { SettingsRoute } from '../../types/settings';
import { Extension } from './Extension';
import { PartnerInfoControl } from './PartnerInfoControl';
import { SnackBarNotificationOptions } from '../../types/SnackBarNotificationOptions';
import { STORAGE_KEY_AGENT_EXTENSION_FROM_API } from '../../constants';
import moment from 'moment';
import { DynamicPageComponent } from './types';
import { createTypedLogger } from '../../utils/typedLogger';

const SidebarRoutes: SettingsRoute[] = [
  {
    key: 'extension',
    text: 'Extension',
    active: false,
  },
  {
    key: 'partner',
    text: 'Partner Information',
    active: false,
  },
];
const StyledRoot = styled('div')(() => ({
  display: 'flex',
  border: '1px solid #ccc',
}));

const StyledContent = styled('div')(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.palette.background.default,
  padding: 0,
}));

const StyledNavContent = styled('div')(() => ({
  border: '1px solid #ccc',
  borderWidth: '0px 0px 0px 1px',
  padding: '0px !important',
}));

const logger = createTypedLogger('Connected-Vehicle-FrontEnd');

export const Settings: DynamicPageComponent = () => {
  const { api } = useApi();
  const { auth } = useAuthentication();
  const { userExtension, saveExtension, userProfile, setUserProfile } =
    useUser();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { agentExtensionLogsData } = useAgentExtension();
  const { withLocalStorage } = useLocalStorage();

  const extensionRef = React.useRef<HTMLInputElement>(null);
  const partnerRef = React.useRef<HTMLInputElement>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [agentExtension, setAgentExtension] = useState<any>({
    extension: userExtension,
  });
  const [clientPlatform, setClientPlatForm] = useState<any>({
    clientPlatform: userProfile?.clientPlatform,
  });

  const [locationKey, setLocationKey] = useState<string | undefined>('');
  const [reason, setReason] = useState<string>('NA');

  useEffect(() => {
    setAgentExtension({
      extension: userExtension,
    });
  }, [userExtension, location.key]);

  const localStorageAgentExtension = localStorage.getItem(
    STORAGE_KEY_AGENT_EXTENSION_FROM_API,
  );

  useEffect(() => {
    setUserEmail(auth.getUserEmail());
  }, [auth]);

  useEffect(() => {
    const saveLoginData = async (data: any): Promise<boolean> => {
      if (data.extension) {
        const misMatchExtension =
          data.extension &&
          localStorageAgentExtension &&
          localStorageAgentExtension !== null &&
          data.extension !== localStorageAgentExtension
            ? 'The assigned extension does not match with the current extension. Because user has changed the extension manually'
            : '';

        const newReason = misMatchExtension ? 'Extension Modified' : reason;

        const loginData: LoginData = {
          email_address: auth.getUserEmail(),
          agent_extension: data.extension,
          extension_logs: data,
          reason: newReason,
        };

        logger.info('Settings:', {
          action: 'SaveLoginData',
          agent_extension: data.extension,
          message: 'Login Details Received',
          userEmail: userEmail,
          loginData: loginData,
          reason: newReason,
        });

        const res = await api.post_logindata(loginData);
        if (res.agent_extension) {
          logger.info('Settings:', {
            action: 'SaveLoginData',
            agent_extension: res.agent_extension,
            message: 'Login Details Saved into DB.',
            userEmail: userEmail,
            reason: newReason,
          });

          saveExtension(res.agent_extension);
          withLocalStorage(
            STORAGE_KEY_AGENT_EXTENSION_FROM_API,
            data.extension,
          );
          return true;
        }
      }
      return false;
    };

    if (
      !userExtension &&
      userProfile &&
      !userProfile?.isComplete &&
      agentExtensionLogsData &&
      agentExtensionLogsData.extension
    ) {
      const currentDateTime = moment(new Date()).utc();

      const checkInDateTime = moment(
        new Date(agentExtensionLogsData.check_in_time),
      ).utc();

      const releaseDateTime = moment(
        new Date(agentExtensionLogsData.release_time),
      ).utc();

      const checkOutDateTime = moment(
        new Date(agentExtensionLogsData.check_out_time),
      ).utc();

      const checkOutDateTimeBefore10 = moment(
        new Date(agentExtensionLogsData.check_out_time),
      ).utc();

      checkOutDateTimeBefore10.subtract(10, 'minute');

      if (
        currentDateTime > checkInDateTime &&
        currentDateTime < checkOutDateTime &&
        currentDateTime < checkOutDateTimeBefore10 &&
        currentDateTime < releaseDateTime
      ) {
        logger.info('Settings:', {
          action: 'get_agent_extension_logs',
          agent_extension: agentExtensionLogsData.extension,
          message:
            'Extension Received from Client, save and display in extension box',
          userEmail: userEmail,
        });
        setAgentExtension({
          extension: agentExtensionLogsData.extension,
        });
        setReason('Extension received');
        saveLoginData(agentExtensionLogsData);
      } else {
        logger.info('Settings:', {
          action: 'get_agent_extension_logs',
          agent_extension: agentExtensionLogsData?.extension,
          case: 'NoCase',
          userEmail: userEmail,
          message: `Please enter your phone extension to start receiving calls. Because might be extension time out is passed out.`,
        });
        setReason('Extension not received');
      }
    } else {
      logger.info('Settings:', {
        action: 'get_agent_extension_logs',
        agent_extension: agentExtensionLogsData?.extension,
        case: 'NoCase',
        userEmail: userEmail,
        message: `Please enter your phone extension to start receiving calls. Because might be extension time out is passed out.`,
      });
      setReason('Extension not received');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agentExtensionLogsData,
    api,
    auth,
    reason,
    saveExtension,
    userEmail,
    userExtension,
    userProfile,
    withLocalStorage,
  ]);

  useEffect(() => {
    setLocationKey(location?.key);
  }, [location.key]);

  useEffect(() => {
    setClientPlatForm({
      clientPlatform: userProfile?.clientPlatform,
    });
  }, [location.key, userProfile?.clientPlatform]);

  const onSidebarChanged = (key: string): void => {
    if (key === 'extension') {
      if (extensionRef && extensionRef.current) {
        extensionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
    if (key === 'partner') {
      if (partnerRef && partnerRef.current) {
        partnerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  const successNotificationOptions: SnackBarNotificationOptions = {
    type: 'success',
    title: 'Data Saved successfully',
  };

  const errorNotificationOptions: SnackBarNotificationOptions = {
    type: 'error',
    title: 'An error occurred. Please refresh the page and try again',
  };

  const onExtensionChangedHandler = async (extn: string): Promise<boolean> => {
    if (extn) {
      const misMatchExtension =
        extn &&
        localStorageAgentExtension &&
        localStorageAgentExtension !== null &&
        extn !== localStorageAgentExtension
          ? 'The assigned extension does not match with the current extension.'
          : '';

      const newReason = misMatchExtension
        ? 'Extension Modified'
        : 'Extension not received';

      const loginData: LoginData = {
        email_address: userEmail,
        agent_extension: extn,
        extension_logs: misMatchExtension
          ? agentExtensionLogsData
          : {
              extension: '',
              check_in_time: '',
              check_out_time: '',
              release_time: '',
              first_name: '',
              last_name: '',
              email: '',
              duration: 0,
              modified_by: '',
              modified_date: '',
              can_release: false,
              can_modify: false,
            },
        reason: newReason,
      };

      logger.info('Settings:', {
        action: 'SaveLoginData',
        agent_extension: extn,
        message: 'Extension Changed Handler Received Extension Details',
        userEmail: userEmail,
        loginData: loginData,
      });
      const res = await api.post_logindata(loginData);
      if (res.agent_extension) {
        logger.info('Settings:', {
          action: 'SaveLoginData',
          agent_extension: res?.agent_extension,
          message: 'Login Details Saved into DB from Extension Changed Handler',
          userEmail: userEmail,
        });

        logger.info('Settings:', {
          action: 'SaveLoginData',
          agent_extension_input: extn,
          message: misMatchExtension,
          userEmail: userEmail,
          agent_extension_from_client: localStorageAgentExtension,
        });

        saveExtension(extn);
        showNotification(successNotificationOptions);
        return true;
      }
      showNotification(errorNotificationOptions);
    }
    return false;
  };

  const onProfileChangedHandler = async (
    clientPlatforms: any,
  ): Promise<boolean> => {
    if (clientPlatforms.length) {
      const isComplete = userProfile?.isComplete
        ? userProfile?.isComplete
        : false;

      const emailAddress = userEmail;

      const profileDataObj: ProfileItem = {
        emailAddress: emailAddress,
        isComplete: isComplete,
        clientPlatform: clientPlatforms,
      };

      if (emailAddress && clientPlatforms.length && isComplete) {
        const putProfileResponse = await api.put_profilesdata(profileDataObj);
        if (putProfileResponse) {
          showNotification(successNotificationOptions);
          setUserProfile(putProfileResponse);
          return true;
        }
      } else {
        const postProfileResponse = await api.post_profilesdata(profileDataObj);
        if (postProfileResponse) {
          showNotification(successNotificationOptions);
          setUserProfile(postProfileResponse);
          return true;
        }
      }
      showNotification(errorNotificationOptions);
    }
    return false;
  };

  return (
    <CvLayout
      headerTitle="Connected Vehicle Portal"
      viewActiveCase="VIEW ACTIVE CASE"
    >
      <StyledRoot>
        <SettingsSidebar
          routes={SidebarRoutes}
          onSidebarChanged={onSidebarChanged}
        />
        <StyledContent id="settingsmain">
          <Grid container justifyContent="flex-start" spacing={0}>
            <Grid item sm={12}>
              <StyledNavContent>
                <div>
                  <div ref={extensionRef} id={locationKey}>
                    <CommonCard title="Extension">
                      <Extension
                        data={agentExtension}
                        onExtensionChanged={onExtensionChangedHandler}
                        locationKey={locationKey}
                      />
                    </CommonCard>
                  </div>
                  <div ref={partnerRef} id={locationKey}>
                    <CommonCard title="Partner Information">
                      <PartnerInfoControl
                        profileData={clientPlatform}
                        onProfileChanged={onProfileChangedHandler}
                        locationKey={locationKey}
                      />
                    </CommonCard>
                  </div>
                </div>
              </StyledNavContent>
            </Grid>
          </Grid>
        </StyledContent>
      </StyledRoot>
    </CvLayout>
  );
};
